import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
import { logUserEvent } from './utils/logger';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import VersionFooter from './components/VersionFooter';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [accessMessage, setAccessMessage] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);

    // Load reCAPTCHA v3 script
    useEffect(() => {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (siteKey && !window.grecaptcha) {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    const executeRecaptcha = async (action) => {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (!siteKey || !window.grecaptcha) {
            console.warn('reCAPTCHA not configured or loaded');
            return null;
        }

        try {
            await new Promise((resolve) => {
                window.grecaptcha.ready(resolve);
            });

            const token = await window.grecaptcha.execute(siteKey, { action });
            return token;
        } catch (error) {
            console.error('reCAPTCHA execution failed:', error);
            return null;
        }
    };

    const checkTrainingAccess = async (email) => {
        try {
            console.log('Checking training access for:', email);
            const response = await axios.post('/api/training/check-access', { email });
            console.log('Access check response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Access check failed:', error);
            console.error('Error details:', error.response?.data || error.message);
            throw error;
        }
    };

    const requestTrainingAccess = async (email) => {
        try {
            // Get reCAPTCHA token
            const recaptchaToken = await executeRecaptcha('training_access_request');

            const response = await axios.post('/api/training/request-access', {
                email,
                recaptcha_token: recaptchaToken
            });

            return response.data;
        } catch (error) {
            console.error('Access request failed:', error);
            throw error;
        }
    };

    const handleSuccess = async (credentialResponse) => {
        setLoading(true);
        setAccessMessage('');
        setAccessDenied(false);

        try {
            const decoded = jwtDecode(credentialResponse.credential);
            console.log('Google Sign-In successful, decoded token:', decoded);

            const userEmail = decoded.email;

            // Step 1: Check if user has training access
            const accessCheck = await checkTrainingAccess(userEmail);

            if (accessCheck.approved) {
                // User is approved - proceed with normal login
                const userData = {
                    email: decoded.email,
                    name: decoded.given_name,
                    surname: decoded.family_name
                };

                const response = await axios.post('/api/auth/google', userData);
                console.log('User data processed by backend:', response.data);

                login(response.data);

                // Log successful login
                localStorage.setItem('userId', response.data.id); // Ensure ID is stored before logging
                await logUserEvent('login_success', 'User logged in successfully');

                navigate('/home');
            } else {
                // User is not approved - log access request and show friendly message
                await requestTrainingAccess(userEmail);

                setAccessDenied(true);
                setAccessMessage(accessCheck.message);
            }

        } catch (error) {
            console.error('Login process failed:', error);
            console.error('Error response:', error.response);
            console.error('Error message:', error.message);

            if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
                setAccessMessage('Cannot connect to server. Please ensure the backend is running and try again.');
            } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('Security verification')) {
                setAccessMessage('Security verification failed. Please try again.');
            } else if (error.response?.status === 404) {
                setAccessMessage('API endpoint not found. Please check if the backend is properly configured.');
            } else {
                setAccessMessage(`Login failed: ${error.response?.data?.detail || error.message || 'Unknown error'}. Please try again.`);
            }
            setAccessDenied(true);
        } finally {
            setLoading(false);
        }
    };

    const handleError = () => {
        console.log('Google Login Failed');
        setAccessMessage('Google login failed. Please try again.');
        setAccessDenied(true);
    };

    console.log('Client ID submitted to Google for button rendering:', process.env.REACT_APP_GOOGLE_CLIENT_ID);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            px: 3,
            background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
        }}>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                <img
                    src="/images/academy_logo_small.png"
                    alt="DeepAlert Academy"
                    style={{
                        height: 'auto',
                        width: '400px', // Adjust based on user preference or previous CSS
                        margin: 'auto',
                    }}
                />
            </Box>

            <Typography variant="h3" component="h1" sx={{
                fontWeight: 'bold',
                color: 'white',
                mb: 2,
                textShadow: '0px 2px 4px rgba(0,0,0,0.6)'
            }}>
                Academy
            </Typography>

            <Typography variant="h5" component="div" sx={{
                fontWeight: 'normal',
                color: 'white',
                mb: 4,
                textDecoration: 'none',
                borderBottom: 'none',
                border: 'none',
                textShadow: '0px 1px 3px rgba(0,0,0,0.5)',
                '&::after': { display: 'none' },
                '&::before': { display: 'none' }
            }}>
                Your centre of excellence
            </Typography>


            <Box sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: 500,
                width: '100%'
            }}>



                {!accessDenied && (
                    <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
                        Please sign in with Google to continue
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography sx={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)' }}>Verifying access...</Typography>
                    </Box>
                )}

                {accessMessage && (
                    <Alert
                        severity={accessDenied ? "info" : "error"}
                        sx={{ mb: 3, textAlign: 'left' }}
                    >
                        {accessMessage}
                    </Alert>
                )}

                {!loading && !accessDenied && (
                    <GoogleLogin
                        onSuccess={handleSuccess}
                        onError={handleError}
                        size="large"
                        theme="outline"
                        text="signin_with"
                    />
                )}

                {accessDenied && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        For immediate assistance, please contact{' '}
                        <a href="mailto:support@deepalert.ai" style={{ color: 'inherit' }}>
                            support@deepalert.ai
                        </a>
                    </Typography>
                )}
            </Box>

            <VersionFooter />
        </Box>
    );
};

export default Login;
